import React, { useState, useEffect } from 'react';
import { agendamentoService } from '../../services';
import { formatDate } from '../../utils/formatters';
import type { Agendamento } from '../../types/entities';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

interface AgendamentoTableProps {
  onEdit?: (agendamento: Agendamento) => void;
  onView?: (agendamento: Agendamento) => void;
  refreshKey?: number;
  onDeleted?: () => void;
}

export const AgendamentoTable: React.FC<AgendamentoTableProps> = ({
  onEdit,
  onView,
  refreshKey,
  onDeleted
}) => {
  const [agendamentos, setAgendamentos] = useState<Agendamento[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState<keyof Agendamento>('data');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTargetItem, setDeleteTargetItem] = useState<Agendamento | null>(null);
  const notifier = useNotifier();

  // Status filters
  const [statusFilters, setStatusFilters] = useState({
    pendente: false,
    aberto: false,
    concluido: false,
    cancelado: false
  });

  const loadAgendamentos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build status filter parameter
      const selectedStatuses = Object.entries(statusFilters)
        .filter(([_, selected]) => selected)
        .map(([status, _]) => status);
      
      const queryParams: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortField,
        sortOrder
      };

      // Add search term if present
      if (searchTerm.trim()) {
        queryParams.search = searchTerm.trim();
      }

      // Add status filter if any status is selected
      if (selectedStatuses.length > 0) {
        queryParams.status = selectedStatuses.join(',');
      }

      const response = await agendamentoService.list(queryParams);
      
      const data = response?.data || response || [];
      if (Array.isArray(data)) {
        setAgendamentos(data);
        setTotalPages(Math.ceil(data.length / itemsPerPage));
      } else if (data.items && Array.isArray(data.items)) {
        setAgendamentos(data.items);
        setTotalPages(data.totalPages || 1);
      } else {
        setAgendamentos([]);
        setTotalPages(1);
      }
    } catch (err: any) {
      console.error('Erro ao carregar agendamentos:', err);
      setError(err.message || 'Erro ao carregar agendamentos');
      setAgendamentos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgendamentos();
  }, [currentPage, searchTerm, sortField, sortOrder, refreshKey, statusFilters]);

  const handleSort = (field: keyof Agendamento) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleDelete = (agendamento: Agendamento) => {
    if (!agendamento.id) return;
    setDeleteTargetItem(agendamento);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetItem?.id) return;
    setConfirmOpen(false);
    try {
      await agendamentoService.remove(deleteTargetItem.id);
      notifier.show({ message: 'Agendamento excluído com sucesso', severity: 'success' });
      loadAgendamentos();
      onDeleted?.();
    } catch (err: any) {
      notifier.show({ message: err.message || 'Erro ao excluir agendamento', severity: 'error' });
    } finally {
      setDeleteTargetItem(null);
    }
  };

  const handleStatusFilterChange = (status: keyof typeof statusFilters) => {
    setStatusFilters(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  return (
    <div className="space-y-4">
      {/* Header e Busca */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xs font-medium" style={{ color: '#3d2b56' }}>
          Agendamentos ({agendamentos.length})
        </h2>

        <div className="flex gap-1 flex-wrap">
          {/* Campo de ordenação */}
          <div className="flex items-center gap-1">
            <label className="text-xs" style={{ color: '#3d2b56' }}>Ordenar por:</label>
            <select
              value={sortField}
              onChange={(e) => {
                setSortField(e.target.value as keyof Agendamento);
                setCurrentPage(1);
              }}
              className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212',
                minWidth: '140px'
              }}
            >
              <option value="data">Data</option>
              <option value="descricaoAgendamento">Descrição</option>
              <option value="status">Status</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-2 py-1 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 text-xs"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
              title={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          
          <form onSubmit={(e) => { e.preventDefault(); setCurrentPage(1); loadAgendamentos(); }} className="flex gap-1">
            <input
              type="text"
              placeholder="Buscar agendamentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
              style={{ 
                borderColor: '#C3B4DE',
                backgroundColor: '#FFFFFF',
                color: '#121212',
                minWidth: '160px'
              }}
            />
            <button
              type="submit"
              disabled={loading}
              className="px-2 py-1 text-xs font-medium text-white rounded-md hover:opacity-80 focus:outline-none focus:ring-1 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: '#7A61B2' }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </form>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-2 py-1 text-xs font-medium border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1"
            style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
          >
            Filtros {showFilters ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {/* Painel de filtros colapsável */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-6">
            <span className="text-sm font-medium" style={{ color: '#3d2b56' }}>Filtrar por status:</span>
            <div className="flex gap-4">
              {Object.entries(statusFilters).map(([status, checked]) => (
                <label key={status} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleStatusFilterChange(status as keyof typeof statusFilters)}
                    className="rounded border-gray-300 focus:ring-1"
                    style={{ accentColor: '#7A61B2' }}
                  />
                  <span className="text-sm capitalize" style={{ color: '#5d54a4' }}>{status}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Ordenação e conteúdo */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-sm" style={{ color: '#7A61B2' }}>Carregando...</div>
        </div>
      ) : error ? (
        <div className="bg-red-50 rounded-lg shadow p-6 text-center">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      ) : (
        <>
          {/* Grid de cards */}
          {agendamentos.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="text-sm text-gray-500">Nenhum agendamento encontrado</div>
            </div>
          ) : (
            <div className="space-y-3">
              {agendamentos.map((agendamento) => {
                let borderColor = '#7A61B2';
                if (agendamento.status === 'ativo') borderColor = '#10B981';
                else if (agendamento.status === 'pendente') borderColor = '#F59E0B';
                else if (agendamento.status === 'concluido') borderColor = '#3B82F6';
                else if (agendamento.status === 'cancelado') borderColor = '#EF4444';

                return (
                  <div
                    key={agendamento.id}
                    className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4"
                    style={{ borderLeft: `4px solid ${borderColor}` }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            agendamento.status === 'ativo' ? 'bg-green-100 text-green-800' :
                            agendamento.status === 'pendente' ? 'bg-yellow-100 text-yellow-800' :
                            agendamento.status === 'concluido' ? 'bg-blue-100 text-blue-800' :
                            agendamento.status === 'cancelado' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {agendamento.status || 'N/A'}
                          </span>
                          <span className="text-xs font-medium" style={{ color: '#3d2b56' }}>
                            {agendamento.data ? formatDate(agendamento.data) : '-'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {onView && (
                          <button
                            onClick={() => onView(agendamento)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="Ver"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={() => onEdit(agendamento)}
                            className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                            title="Editar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(agendamento)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                          title="Excluir"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="font-medium" style={{ color: '#7A61B2' }}>Descrição:</span>
                        <div className="text-gray-700 mt-1">{agendamento.descricaoAgendamento || '-'}</div>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: '#7A61B2' }}>Usuário Origem:</span>
                        <div className="text-gray-700 mt-1">{agendamento.usuarioOrigem?.nome || agendamento.usuarioOrigemId || '-'}</div>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: '#7A61B2' }}>Usuário Destino:</span>
                        <div className="text-gray-700 mt-1">{agendamento.usuarioDestino?.nome || agendamento.usuarioDestinoId || '-'}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between px-4 py-3 bg-white rounded-lg shadow">
              <span className="text-xs" style={{ color: '#5d54a4' }}>
                Página {currentPage} de {totalPages}
              </span>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Excluir Agendamento"
        message="Tem certeza que deseja excluir este agendamento?"
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="red"
        onConfirm={handleConfirmDelete}
        onCancel={() => { setConfirmOpen(false); setDeleteTargetItem(null); }}
      />
    </div>
  );
};